import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';

export interface IAvailableDateTimeFormat {
    relatedLanguageTypes: FiApplicationLanguageType[];
    locale: AvailableDateTimeLocale;
    options: Intl.DateTimeFormatOptions;
    validationRegExp: RegExp;
    label: string;
}

export enum AvailableDateTimeLocale {
    Swedish = 'sv',
    Italian = 'it-IT',
    English = 'en',
    Norwegian = 'no-NO',
}

export const AvailableDateFormats: IAvailableDateTimeFormat[] = [
    {
        relatedLanguageTypes: [
            FiApplicationLanguageType.Swedish,
            FiApplicationLanguageType.Chinese,
            FiApplicationLanguageType.Mongol,
            FiApplicationLanguageType.Arabic,
            FiApplicationLanguageType.Vietnamese,
            FiApplicationLanguageType.Icelandic,
            FiApplicationLanguageType.Turkish,
        ],
        locale: AvailableDateTimeLocale.Swedish,
        options: { year: 'numeric', month: '2-digit', day: '2-digit' },
        label: 'YYYY-MM-DD',
        validationRegExp: /^(\d{4})-(\d{2})-(\d{2})$/,
    },
    {
        relatedLanguageTypes: [FiApplicationLanguageType.Italian, FiApplicationLanguageType.French],
        locale: AvailableDateTimeLocale.Italian,
        options: { year: 'numeric', month: '2-digit', day: '2-digit' },
        label: 'DD/MM/YYYY',
        validationRegExp: /^(\d{2})\/(\d{2})\/(\d{4})$/,
    },
    {
        relatedLanguageTypes: [FiApplicationLanguageType.English, FiApplicationLanguageType.Spanish],
        locale: AvailableDateTimeLocale.English,
        options: { year: 'numeric', month: '2-digit', day: '2-digit' },
        label: 'MM/DD/YYYY',
        validationRegExp: /^(\d{2})\/(\d{2})\/(\d{4})$/,
    },
    {
        relatedLanguageTypes: [
            FiApplicationLanguageType.Norwegian,
            FiApplicationLanguageType.Danish,
            FiApplicationLanguageType.Finnish,
            FiApplicationLanguageType.German,
            FiApplicationLanguageType.Estonian,
        ],
        locale: AvailableDateTimeLocale.Norwegian,
        options: { year: '2-digit', month: '2-digit', day: '2-digit' },
        label: 'DD.MM.YY',
        validationRegExp: /^(.*)$/, // todo
    },
];

export const AvailableTimeFormats: IAvailableDateTimeFormat[] = [
    {
        relatedLanguageTypes: [
            FiApplicationLanguageType.Swedish,
            FiApplicationLanguageType.Danish,
            FiApplicationLanguageType.Norwegian,
            FiApplicationLanguageType.Finnish,
            FiApplicationLanguageType.French,
            FiApplicationLanguageType.Estonian,
            FiApplicationLanguageType.Spanish,
            FiApplicationLanguageType.German,
            FiApplicationLanguageType.Italian,
            FiApplicationLanguageType.Chinese,
            FiApplicationLanguageType.Mongol,
            FiApplicationLanguageType.Catalan,
            FiApplicationLanguageType.Dutch,
            FiApplicationLanguageType.Arabic,
            FiApplicationLanguageType.Vietnamese,
            FiApplicationLanguageType.Icelandic,
            FiApplicationLanguageType.Turkish,
        ],
        locale: AvailableDateTimeLocale.Swedish,
        options: { hour: '2-digit', minute: '2-digit', hour12: false },
        label: 'time.24hr_clock',
        validationRegExp: /^(\d{4})-(\d{2})-(\d{2})$/,
    },
    {
        relatedLanguageTypes: [FiApplicationLanguageType.English],
        locale: AvailableDateTimeLocale.English,
        options: { hour: '2-digit', minute: '2-digit', hour12: true },
        label: 'time.12hr_clock',
        validationRegExp: /^(.*)$/, // todo
    },
];

export const FiDateFormatOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
};

export const FiTimeFormatOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    //hour12: false
};

export const FiDateTimeFormatOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    //hour12: false
};

export const FiDateTimeSecFormatOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    //hour12: false
};

export const FiCommentDateTimeFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    //hour12: false
};

// https://chainformation.atlassian.net/browse/FI-5891
// FiMonthDateFormatOptions should not be used
/*export const FiMonthDateFormatOptions: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "2-digit"
}*/

export const FiMonthNoYearDateFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
    year: undefined,
};

/*export const FiLongMonthDateFormatOptions: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "2-digit"
}*/

export const FiShortWeekdayDateFormatOptions: Intl.DateTimeFormatOptions = {
    year: undefined,
    month: undefined,
    day: undefined,
    weekday: 'short',
};

export const FiShortMonthDateFormatOptions: Intl.DateTimeFormatOptions = {
    year: undefined,
    day: undefined,
    month: 'short',
};

export const FI_FORMAT_DATE_REGISTRY = {
    date: FiDateFormatOptions,
    time: FiTimeFormatOptions,
    'date-time': FiDateTimeFormatOptions,
    'date-time-sec': FiDateTimeSecFormatOptions,
    //'month-date': FiMonthDateFormatOptions,
    //'long-month-date': FiLongMonthDateFormatOptions,
    'weekday-short': FiShortWeekdayDateFormatOptions,
    'month-short': FiShortMonthDateFormatOptions,
    'month-date-no-year': FiMonthNoYearDateFormatOptions,
};

export interface IFormatDateArgs {
    showToday?: boolean;
    showUTC?: boolean;
    applicationLanguageType?: FiApplicationLanguageType;
}

export const DefaultFormatDateArgs: IFormatDateArgs = {
    showToday: true,
    applicationLanguageType: FiApplicationLanguageType.Swedish,
};

export function fiGetUserPreferredDateFormat(applicationLanguageType: number): IAvailableDateTimeFormat {
    return AvailableDateFormats.find((availableDateFormat: IAvailableDateTimeFormat) => {
        return availableDateFormat.relatedLanguageTypes.includes(applicationLanguageType);
    });
}

export function fiGetUserPreferredTimeFormat(applicationLanguageType: number): IAvailableDateTimeFormat {
    return AvailableTimeFormats.find((availableTimeFormat: IAvailableDateTimeFormat) => {
        return availableTimeFormat.relatedLanguageTypes.includes(applicationLanguageType);
    });
}

export function fiGetLocaleCode(languageId: number) {
    switch (languageId) {
        case FiApplicationLanguageType.Swedish:
            return 'sv';
        case FiApplicationLanguageType.English:
            return 'en';
        case FiApplicationLanguageType.Danish:
            return 'da';
        case FiApplicationLanguageType.Norwegian:
            return 'nb-no';
        case FiApplicationLanguageType.Finnish:
            return 'fi';
        case FiApplicationLanguageType.French:
            return 'fr';
        case FiApplicationLanguageType.Estonian:
            return 'et';
        case FiApplicationLanguageType.Spanish:
            return 'es';
        case FiApplicationLanguageType.German:
            return 'de';
        case FiApplicationLanguageType.Italian:
            return 'it';
        case FiApplicationLanguageType.Chinese:
            return 'zh';
        case FiApplicationLanguageType.Catalan:
            return 'ca';
        case FiApplicationLanguageType.Dutch:
            return 'nl';
        default:
            return 'en';
    }
}
